import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { WORKFLOWS, MODULES } from '../utils/constants';
import { api } from '../api/client';
import { formatMoney, daysSince } from '../utils/formatters';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';

// ─── Dashboard data-extraction helpers ───

const DASHBOARD_KEY_MAP = {
  'credits': 'credits',
  'unreturned-cores': 'unreturned_cores',
  'never-received': 'ordered_never_received',
  'missing-invoices': 'missing_invoices',
  'cost-discrepancies': 'cost_discrepancies',
  'invalid-ro': 'invalid_ro_refs',
  'duplicate-orders': null,
  'received-not-on-ticket': null,
  'vendor-price-trends': null,
  'cod-reconciliation': null,
};

const DOLLAR_FIELDS = [
  'total_outstanding_usd', 'total_exposure_usd', 'total_usd',
  'total_cost_usd', 'total_variance_usd', 'total_core_value_usd',
];

function getModuleDollars(d) {
  if (!d) return 0;
  for (const f of DOLLAR_FIELDS) {
    if (d[f] != null) return d[f];
  }
  return 0;
}

function getModuleCount(d) {
  if (!d) return 0;
  return d.count ?? d.items?.length ?? 0;
}

// ─── Extended module fetcher ───

function useExtendedModules() {
  const [extData, setExtData] = useState({});
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    const extKeys = ['duplicate-orders', 'received-not-on-ticket', 'vendor-price-trends', 'cod-reconciliation'];
    const results = {};
    await Promise.allSettled(
      extKeys.map(async (key) => {
        const mod = MODULES[key];
        if (mod?.endpoint) {
          const data = await api.getModule(mod.endpoint);
          results[key] = data;
        }
      })
    );
    setExtData(results);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { extData, extLoading: loading, refreshExt: fetch };
}

// ─── Workflow Section Component ───

function WorkflowSection({ workflow, moduleStats, onModuleClick }) {
  const wf = WORKFLOWS[workflow];
  const mods = wf.modules.map((key) => ({ ...MODULES[key], stats: moduleStats[key] }));
  const totalDollars = mods.reduce((s, m) => s + (m.stats?.dollars || 0), 0);
  const totalCount = mods.reduce((s, m) => s + (m.stats?.count || 0), 0);

  return (
    <div className="mb-8 animate-fade-in">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
            style={{ background: wf.accentBg, color: wf.accent }}
          >
            {wf.icon}
          </span>
          <div>
            <h2 className="text-sm font-extrabold text-stone-800 tracking-tight">{wf.label}</h2>
            <p className="text-[11px] text-stone-400">{wf.tagline}</p>
          </div>
        </div>
        {totalCount > 0 && (
          <div className="text-right">
            <div className="text-base font-extrabold tabular-nums" style={{ color: wf.accent }}>
              {formatMoney(totalDollars)}
            </div>
            <div className="text-[10px] text-stone-400 tabular-nums">{totalCount} items</div>
          </div>
        )}
      </div>

      {/* Module cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {mods.map((m) => {
          const count = m.stats?.count || 0;
          const dollars = m.stats?.dollars || 0;
          const hasIssues = count > 0;

          return (
            <button
              key={m.key}
              onClick={() => onModuleClick(m.key)}
              className={`text-left rounded-xl p-4 border transition-all group ${
                hasIssues
                  ? 'bg-white border-stone-200 hover:shadow-md hover:border-stone-300'
                  : 'bg-stone-100/50 border-stone-200/60'
              }`}
              style={hasIssues ? { borderLeftWidth: '3px', borderLeftColor: wf.accent } : {}}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="text-[13px] font-bold text-stone-700 group-hover:text-stone-900">
                  {m.label}
                </div>
                {hasIssues && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-extrabold tabular-nums"
                    style={{ background: wf.accentBg, color: wf.accent }}
                  >
                    {count}
                  </span>
                )}
              </div>

              <div className="text-[11px] text-stone-400 mb-2">{m.description}</div>

              {hasIssues ? (
                <div className="text-lg font-extrabold tabular-nums" style={{ color: wf.accent }}>
                  {formatMoney(dollars)}
                </div>
              ) : (
                <div className="text-xs font-semibold text-emerald-600">All clear</div>
              )}

              {/* Aging indicator for recovery modules */}
              {m.stats?.alertCount > 0 && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 pulse-alert" />
                  <span className="text-[10px] font-bold text-red-600">
                    {m.stats.alertCount} overdue (30+ days)
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Priority Action Queue ───

function PriorityQueue({ items, onItemClick }) {
  if (!items || items.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-extrabold text-stone-800">Priority Actions</h2>
        <span className="text-[10px] text-stone-400 font-semibold">
          Sorted by urgency &amp; dollar amount
        </span>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden divide-y divide-stone-100">
        {items.slice(0, 15).map((item, i) => {
          const wf = WORKFLOWS[MODULES[item.moduleKey]?.workflow];
          return (
            <button
              key={`${item.moduleKey}-${item.id || i}`}
              onClick={() => onItemClick(item.moduleKey)}
              className="w-full text-left px-5 py-3.5 hover:bg-stone-50 transition-colors flex items-center gap-4"
              style={{ animationDelay: `${i * 30}ms` }}
            >
              <span
                className="w-1 h-8 rounded-full flex-shrink-0"
                style={{ background: wf?.accent || '#9CA3AF' }}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: wf?.accent }}>
                    {MODULES[item.moduleKey]?.label}
                  </span>
                  {item.vendor && (
                    <span className="text-[10px] text-stone-400">
                      {item.vendor}
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold text-stone-700 truncate">
                  {item.title}
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <div className="text-sm font-extrabold text-stone-800 tabular-nums">
                  {formatMoney(item.dollars)}
                </div>
                {item.aging > 0 && (
                  <div className={`text-[10px] font-bold tabular-nums ${
                    item.aging >= 45 ? 'text-red-600' : item.aging >= 30 ? 'text-amber-600' : 'text-stone-400'
                  }`}>
                    {item.aging}d ago
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Exposure Banner ───

function ExposureBanner({ totalDollars, totalIssues, agingBreakdown }) {
  return (
    <div className="mb-8 bg-[#1C1917] rounded-2xl p-6 text-stone-100">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-[10px] text-stone-500 font-bold tracking-[2px] uppercase mb-1">
            Total Exposure
          </div>
          <div className="text-3xl font-extrabold tracking-tight tabular-nums">
            {formatMoney(totalDollars)}
          </div>
          <div className="text-xs text-stone-500 mt-1 tabular-nums">
            across {totalIssues} unresolved items
          </div>
        </div>

        <div className="flex gap-6">
          <div className="text-center">
            <div className="text-xl font-extrabold text-emerald-400 tabular-nums">
              {agingBreakdown.fresh}
            </div>
            <div className="text-[10px] text-stone-500 font-semibold">&lt;30 days</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold text-amber-400 tabular-nums">
              {agingBreakdown.warning}
            </div>
            <div className="text-[10px] text-stone-500 font-semibold">30-44 days</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-extrabold text-red-400 tabular-nums">
              {agingBreakdown.critical}
            </div>
            <div className="text-[10px] text-stone-500 font-semibold">45+ days</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard Page ───

export default function DashboardPage() {
  const { dashboard, dashboardLoading, refreshDashboard } = useOutletContext();
  const { extData, extLoading } = useExtendedModules();
  const navigate = useNavigate();

  if (dashboardLoading && !dashboard) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }
  if (!dashboard) {
    return <ErrorState message="Could not load dashboard data" onRetry={refreshDashboard} />;
  }

  // Build module stats from dashboard + extended data
  const moduleStats = {};
  Object.keys(MODULES).forEach((key) => {
    const dashKey = DASHBOARD_KEY_MAP[key];
    const d = dashKey ? dashboard[dashKey] : extData[key];
    const count = getModuleCount(d);
    const dollars = getModuleDollars(d);

    // Count alerts (items older than 30 days)
    let alertCount = 0;
    if (d?.items) {
      alertCount = d.items.filter((item) => {
        const dateField = item.return_date || item._returnDate || item.ordered_date || item.order_date || item.created_at;
        return dateField && daysSince(dateField) >= 30;
      }).length;
    } else if (d?.alert_count != null) {
      alertCount = d.alert_count;
    }

    moduleStats[key] = { count, dollars, alertCount };
  });

  // Calculate totals and aging
  const totalIssues = Object.values(moduleStats).reduce((s, m) => s + m.count, 0);
  const totalDollars = Object.values(moduleStats).reduce((s, m) => s + m.dollars, 0);

  // Build aging breakdown from all items
  const agingBreakdown = { fresh: 0, warning: 0, critical: 0 };
  const allModuleKeys = Object.keys(DASHBOARD_KEY_MAP);
  allModuleKeys.forEach((key) => {
    const dashKey = DASHBOARD_KEY_MAP[key];
    const d = dashKey ? dashboard[dashKey] : extData[key];
    if (d?.items) {
      d.items.forEach((item) => {
        const dateField = item.return_date || item._returnDate || item.ordered_date || item.order_date || item.created_at;
        if (dateField) {
          const aging = daysSince(dateField);
          if (aging >= 45) agingBreakdown.critical++;
          else if (aging >= 30) agingBreakdown.warning++;
          else agingBreakdown.fresh++;
        } else {
          agingBreakdown.fresh++;
        }
      });
    }
  });

  // Build priority action queue
  const priorityItems = [];
  allModuleKeys.forEach((key) => {
    const dashKey = DASHBOARD_KEY_MAP[key];
    const d = dashKey ? dashboard[dashKey] : extData[key];
    if (d?.items) {
      d.items.forEach((item) => {
        const dateField = item.return_date || item._returnDate || item.ordered_date || item.order_date || item.created_at;
        const aging = dateField ? daysSince(dateField) : 0;
        const dollars = item.total_usd || item.returned_total_usd || item.cost_usd || item.part_cost_usd || item.variance_usd || item.core_charge_usd || 0;

        priorityItems.push({
          moduleKey: key,
          id: item.id || item.return_order_id || item.po_id,
          title: item.part_name || item.partNamePrimary || item.description || item.part_number || `${MODULES[key]?.label} item`,
          vendor: item.vendor_name || item.vendorName,
          dollars: Math.abs(dollars),
          aging,
          // Priority score: weight dollars and aging
          score: Math.abs(dollars) * (1 + aging / 30),
        });
      });
    }
  });
  priorityItems.sort((a, b) => b.score - a.score);

  const handleModuleClick = (moduleKey) => {
    navigate(`/module/${moduleKey}`);
  };

  return (
    <div>
      <ExposureBanner
        totalDollars={totalDollars}
        totalIssues={totalIssues}
        agingBreakdown={agingBreakdown}
      />

      {/* Workflow sections */}
      <div className="stagger-children">
        {Object.keys(WORKFLOWS).map((wfKey) => (
          <WorkflowSection
            key={wfKey}
            workflow={wfKey}
            moduleStats={moduleStats}
            onModuleClick={handleModuleClick}
          />
        ))}
      </div>

      {/* Priority action queue */}
      <PriorityQueue items={priorityItems} onItemClick={handleModuleClick} />

      {/* Vendor Breakdown */}
      <VendorBreakdown dashboard={dashboard} extData={extData} onModuleClick={handleModuleClick} />
    </div>
  );
}

// ─── Vendor Breakdown ───

function VendorBreakdown({ dashboard, extData }) {
  const vendorMap = useMemo(() => {
    const map = {};
    const allKeys = Object.keys(DASHBOARD_KEY_MAP);
    allKeys.forEach((key) => {
      const dashKey = DASHBOARD_KEY_MAP[key];
      const d = dashKey ? dashboard?.[dashKey] : extData?.[key];
      if (d?.items) {
        d.items.forEach((item) => {
          const name = item.vendor_name || item.vendorName || 'Unknown';
          if (!map[name]) map[name] = { name, totalDollars: 0, count: 0, modules: new Set() };
          map[name].totalDollars += Math.abs(item.total_usd || item.returned_total_usd || item.cost_usd || item.part_cost_usd || item.variance_usd || item.core_charge_usd || 0);
          map[name].count++;
          map[name].modules.add(MODULES[key]?.label || key);
        });
      }
    });
    return Object.values(map)
      .sort((a, b) => b.totalDollars - a.totalDollars)
      .slice(0, 10);
  }, [dashboard, extData]);

  if (vendorMap.length === 0) return null;

  return (
    <div className="mb-8">
      <h2 className="text-sm font-extrabold text-stone-800 mb-3">Top Vendors by Exposure</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {vendorMap.map((v) => (
          <div
            key={v.name}
            className="bg-white rounded-xl border border-stone-200 p-4"
          >
            <div className="text-[13px] font-bold text-stone-700 truncate mb-1">{v.name}</div>
            <div className="text-base font-extrabold text-stone-800 tabular-nums mb-1">
              {formatMoney(v.totalDollars)}
            </div>
            <div className="text-[10px] text-stone-400">
              {v.count} item{v.count !== 1 ? 's' : ''} across {v.modules.size} module{v.modules.size !== 1 ? 's' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
